import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, HardDrive, Sparkles, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import AdminNav from "../components/AdminNav";

/**
 * Admin-only rescue console for recordings whose owning user has no email set
 * (typically an anonymous device that later lost localStorage — the recording
 * can no longer be reached by the original owner).
 *
 * Access is enforced entirely by the backend (routers/admin.py, gated by the
 * `ADMIN_EMAILS` env). We don't guard the route client-side; instead we
 * gracefully render the API's own 403/404 responses so a non-admin who somehow
 * lands here still sees a clean message instead of a mysterious blank screen.
 */
export default function AdminOrphans() {
  const [state, setState] = useState({ status: "loading", orphans: [] });
  // Per-row target-email drafts, keyed by meeting_id.
  const [drafts, setDrafts] = useState({});
  // Per-row "reassigning…" flags so double-clicks can't fire two calls.
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/admin/orphans");
      setState({ status: "ok", orphans: r.data.orphans || [] });
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) setState({ status: "forbidden", orphans: [] });
      else if (code === 404) setState({ status: "disabled", orphans: [] });
      else {
        console.error("admin orphans load failed", e);
        setState({ status: "error", orphans: [] });
      }
    }
  };

  useEffect(() => {
    load();
    // Keep the list fresh if the admin leaves this page open (someone else
    // may reassign in a second tab; we don't want stale rows). 30s is cheap.
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  const reassign = async (meetingId) => {
    const email = (drafts[meetingId] || "").trim();
    if (!email) {
      toast.error("Enter a target email first.");
      return;
    }
    setBusy((prev) => ({ ...prev, [meetingId]: true }));
    try {
      const r = await api.post(`/admin/orphans/${meetingId}/reassign`, { target_email: email });
      const items = r?.data?.action_items_reassigned ?? 0;
      toast.success(
        `Reassigned to ${email}` + (items ? ` · ${items} action item${items === 1 ? "" : "s"}` : "")
      );
      // Optimistic remove; a background refresh keeps us honest.
      setState((s) => ({ ...s, orphans: s.orphans.filter((o) => o.meeting_id !== meetingId) }));
      setDrafts((d) => {
        const next = { ...d };
        delete next[meetingId];
        return next;
      });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === "string" ? d : d?.message) || "Reassign failed.";
      toast.error(msg);
    } finally {
      setBusy((prev) => ({ ...prev, [meetingId]: false }));
    }
  };

  return (
    <>
      <AdminNav />
      <div className="max-w-6xl mx-auto px-6 pb-16" data-testid="admin-orphans-page">
        <div className="mt-2">
          <h1 className="font-serif text-4xl">Orphaned recordings</h1>
          <p className="text-sm text-[#888] max-w-2xl mt-2 leading-relaxed">
            A recording lands here when its owning user was anonymous — likely a
            device that lost <code className="text-[#c89f45]">localStorage</code>{" "}
            before they signed in. Reassign the session to any existing account
            by email and its recording + action items follow.
          </p>
        </div>

      {state.status === "loading" && (
        <div className="surface rounded-md p-10 mt-8 text-center text-sm text-[#888]" data-testid="admin-orphans-loading">
          Loading…
        </div>
      )}

      {state.status === "forbidden" && (
        <div
          className="surface rounded-md p-8 mt-8 border-red-900/40 text-sm text-[#eaeaea] flex gap-3 items-start"
          data-testid="admin-orphans-forbidden"
        >
          <AlertTriangle size={18} className="text-red-400 mt-0.5" />
          <div>
            <p className="text-base">Not authorised.</p>
            <p className="text-[#888] mt-1">
              This console is limited to accounts in{" "}
              <code className="text-[#c89f45]">ADMIN_EMAILS</code>. Sign in with
              an admin account first.
            </p>
          </div>
        </div>
      )}

      {state.status === "disabled" && (
        <div
          className="surface rounded-md p-8 mt-8 text-sm text-[#eaeaea] flex gap-3 items-start"
          data-testid="admin-orphans-disabled"
        >
          <AlertTriangle size={18} className="text-[#c89f45] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-base">Admin console disabled on this environment.</p>
            <p className="text-[#888] mt-2">
              This page unlocks once the backend env has an{" "}
              <code className="text-[#c89f45]">ADMIN_EMAILS</code> allow-list.
              Add the following key (comma-separated for multiple admins) and
              redeploy:
            </p>
            <pre className="mt-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 font-mono text-[11px] text-[#c89f45] overflow-x-auto">
              ADMIN_EMAILS=admin@foundtheroom.com
            </pre>
            <p className="text-[11px] text-[#666] mt-2 font-mono">
              Preview vars don't auto-copy to prod. If you can't find where to
              set backend env on <code className="text-[#888]">foundtheroom.com</code>,
              ping Emergent Support with the line above.
            </p>
          </div>
        </div>
      )}

      {state.status === "error" && (
        <div className="surface rounded-md p-8 mt-8 text-sm text-[#eaeaea]" data-testid="admin-orphans-error">
          Could not load orphans.{" "}
          <button className="text-[#c89f45] underline" onClick={load} data-testid="admin-orphans-retry">
            Retry
          </button>
        </div>
      )}

      {state.status === "ok" && state.orphans.length === 0 && (
        <div
          className="surface rounded-md p-10 mt-8 text-center"
          data-testid="admin-orphans-empty"
        >
          <Sparkles size={20} className="text-[#c89f45] mx-auto mb-3" />
          <p className="text-base">The vault is clean.</p>
          <p className="text-sm text-[#888] mt-1">No orphaned recordings right now.</p>
        </div>
      )}

      {state.status === "ok" && state.orphans.length > 0 && (
        <>
          <div className="flex items-center justify-between mt-8 mb-3">
            <p className="text-xs font-mono uppercase tracking-widest text-[#888]">
              {state.orphans.length} ORPHAN{state.orphans.length === 1 ? "" : "S"}
            </p>
            <button
              className="text-xs font-mono uppercase tracking-widest text-[#888] hover:text-[#c89f45]"
              onClick={load}
              data-testid="admin-orphans-refresh"
            >
              REFRESH
            </button>
          </div>

          <ul className="space-y-3" data-testid="admin-orphans-list">
            {state.orphans.map((o) => (
              <li
                key={o.meeting_id}
                className="surface rounded-md p-5"
                data-testid={`admin-orphan-${o.meeting_id}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <HardDrive size={13} className="text-[#c89f45] shrink-0" />
                      <p className="text-sm font-medium truncate">{o.title || "(untitled)"}</p>
                      {o.has_minutes && (
                        <span className="text-[9px] font-mono uppercase tracking-widest text-[#c89f45] border border-[#c89f45]/40 rounded px-1.5 py-0.5">
                          MINUTES
                        </span>
                      )}
                      {o.has_transcript && (
                        <span className="text-[9px] font-mono uppercase tracking-widest text-[#888] border border-[#2a2a2a] rounded px-1.5 py-0.5">
                          TRANSCRIPT
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-[#666] mt-2 space-y-0.5">
                      <p>OWNER · {o.owner_id}</p>
                      <p>
                        MEETING · <span className="text-[#888]">{o.meeting_id}</span>
                      </p>
                      <p>
                        CREATED ·{" "}
                        <span className="text-[#888]">
                          {o.created_at ? new Date(o.created_at).toLocaleString() : "unknown"}
                        </span>
                        {o.duration_seconds ? (
                          <>
                            {" "}· <span className="text-[#888]">{Math.round(o.duration_seconds / 60)}m</span>
                          </>
                        ) : null}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:min-w-[380px]">
                    <input
                      type="email"
                      value={drafts[o.meeting_id] || ""}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [o.meeting_id]: e.target.value }))
                      }
                      placeholder="target@email.com"
                      className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm font-mono text-[#eaeaea] placeholder:text-[#4a4a4a] focus:outline-none focus:border-[#c89f45]"
                      data-testid={`admin-orphan-email-${o.meeting_id}`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") reassign(o.meeting_id);
                      }}
                    />
                    <Button
                      onClick={() => reassign(o.meeting_id)}
                      disabled={!!busy[o.meeting_id] || !(drafts[o.meeting_id] || "").trim()}
                      className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-9 px-3 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`admin-orphan-reassign-${o.meeting_id}`}
                    >
                      {busy[o.meeting_id] ? "Reassigning…" : "Reassign"}{" "}
                      <ArrowUpRight size={14} className="ml-1" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      </div>
    </>
  );
}
